/**
 * 颜色 Design Token 系统
 * 基于创美药业设计体系规范 - #2563EB 现代蓝的完整色彩体系
 * 严格遵循 Ant Design 5.x Design Token 架构
 */

import type { ColorTokens } from './types';

// 全局颜色 Token (Global Color Tokens)
// 基于创美药业设计体系规范的完整色彩定义
const globalColors = {
  // 主色调 - 现代蓝系 (基于 #2563EB)
  blue50: '#EFF6FF',   // 极浅蓝 - 背景色，用于浅色背景
  blue100: '#DBEAFE',  // 浅蓝 - 浅色背景，用于悬停背景
  blue200: '#BFDBFE',  // 较浅蓝 - 边框色
  blue300: '#93C5FD',  // 中浅蓝 - 禁用状态
  blue400: '#60A5FA',  // 中蓝 - 次要按钮
  blue500: '#2563EB',  // 主色 - 现代蓝，品牌主色
  blue600: '#1D4ED8',  // 深蓝 - 悬停状态，主要交互
  blue700: '#1E40AF',  // 更深蓝 - 激活状态
  blue800: '#1E3A8A',  // 深蓝 - 强调色
  blue900: '#1E293B',  // 极深蓝 - 深色文本
  
  // 辅助色系 - 深蓝 (专业权威)
  darkBlue50: '#F0F4F8',
  darkBlue500: '#1B4B8C',  // 深蓝 - 辅助色
  darkBlue600: '#164A87',  // 深蓝悬停
  darkBlue700: '#134082',  // 深蓝激活
  
  // 功能色系 - 医疗绿 (健康生命)
  green50: '#ECFDF5',   // 极浅绿背景
  green100: '#D1FAE5',  // 浅绿背景
  green500: '#2E8B57',  // 医疗绿 - 创新绿
  green600: '#059669',  // 深绿悬停
  green700: '#047857',  // 深绿激活
  
  // 功能色系 - 专业橙 (活力创新)
  orange50: '#FFF7ED',  // 极浅橙背景
  orange100: '#FFEDD5', // 浅橙背景
  orange500: '#FF6B35', // 专业橙 - 活力橙
  orange600: '#EA580C', // 深橙悬停
  orange700: '#C2410C', // 深橙激活
  
  // 功能色系 - 医用红 (警告错误)
  red50: '#FEF2F2',     // 极浅红背景
  red100: '#FEE2E2',    // 浅红背景
  red500: '#EF4444',    // 医用红 - 错误色
  red600: '#DC2626',    // 深红悬停
  red700: '#B91C1C',    // 深红激活
  
  // 中性色阶 - 基于设计规范的灰度系统
  gray50: '#F8FAFC',    // 极浅灰 - 页面背景
  gray100: '#F1F5F9',   // 浅灰 - 区块背景
  gray200: '#E2E8F0',   // 较浅灰 - 边框色
  gray300: '#CBD5E1',   // 中浅灰 - 分割线
  gray400: '#94A3B8',   // 中灰 - 禁用文本
  gray500: '#64748B',   // 深中灰 - 辅助文本
  gray600: '#475569',   // 深灰 - 次要文本
  gray700: '#334155',   // 更深灰 - 标题文本
  gray800: '#1E293B',   // 极深灰 - 主要文本
  gray900: '#0F172A',   // 最深灰 - 强调文本
  
  // 基础色
  white: '#FFFFFF',     // 纯白
  black: '#000000',     // 纯黑
  
  // 透明度变体 (用于遮罩和半透明效果)
  blackAlpha: {
    50: 'rgba(0, 0, 0, 0.04)',
    100: 'rgba(0, 0, 0, 0.06)',
    200: 'rgba(0, 0, 0, 0.08)',
    300: 'rgba(0, 0, 0, 0.16)',
    400: 'rgba(0, 0, 0, 0.24)',
    500: 'rgba(0, 0, 0, 0.36)',
    600: 'rgba(0, 0, 0, 0.48)',
    700: 'rgba(0, 0, 0, 0.64)',
    800: 'rgba(0, 0, 0, 0.80)',
    900: 'rgba(0, 0, 0, 0.92)',
  },
  
  whiteAlpha: {
    50: 'rgba(255, 255, 255, 0.04)',
    100: 'rgba(255, 255, 255, 0.06)',
    200: 'rgba(255, 255, 255, 0.08)',
    300: 'rgba(255, 255, 255, 0.16)',
    400: 'rgba(255, 255, 255, 0.24)',
    500: 'rgba(255, 255, 255, 0.36)',
    600: 'rgba(255, 255, 255, 0.48)',
    700: 'rgba(255, 255, 255, 0.64)',
    800: 'rgba(255, 255, 255, 0.80)',
    900: 'rgba(255, 255, 255, 0.92)',
  },
} as const;

// 别名颜色 Token (Alias Color Tokens)
// 基于创美药业设计体系规范的语义化颜色定义
const aliasColors = {
  // 品牌色别名 - 基于现代蓝 #2563EB
  primary: globalColors.blue500,        // #2563EB - 主品牌色
  primaryHover: globalColors.blue600,   // #1D4ED8 - 悬停状态
  primaryActive: globalColors.blue700,  // #1E40AF - 激活状态
  primaryBg: globalColors.blue50,       // #EFF6FF - 主色背景
  primaryBorder: globalColors.blue200,  // #BFDBFE - 主色边框
  primaryText: globalColors.blue600,    // #1D4ED8 - 主色文本
  
  // 辅助色别名 - 深蓝系
  secondary: globalColors.darkBlue500,  // #1B4B8C - 辅助色
  secondaryHover: globalColors.darkBlue600, // #164A87 - 辅助色悬停
  secondaryActive: globalColors.darkBlue700, // #134082 - 辅助色激活
  secondaryBg: globalColors.darkBlue50, // #F0F4F8 - 辅助色背景
  
  // 语义色别名 - 功能状态色
  success: globalColors.green500,       // #2E8B57 - 成功色 (创新绿)
  successHover: globalColors.green600,  // #059669 - 成功色悬停
  successActive: globalColors.green700, // #047857 - 成功色激活
  successBg: globalColors.green50,      // #ECFDF5 - 成功色背景
  successBorder: globalColors.green100, // #D1FAE5 - 成功色边框
  
  warning: globalColors.orange500,      // #FF6B35 - 警告色 (活力橙)
  warningHover: globalColors.orange600, // #EA580C - 警告色悬停
  warningActive: globalColors.orange700, // #C2410C - 警告色激活
  warningBg: globalColors.orange50,     // #FFF7ED - 警告色背景
  warningBorder: globalColors.orange100, // #FFEDD5 - 警告色边框
  
  error: globalColors.red500,           // #EF4444 - 错误色 (医用红)
  errorHover: globalColors.red600,      // #DC2626 - 错误色悬停
  errorActive: globalColors.red700,     // #B91C1C - 错误色激活
  errorBg: globalColors.red50,          // #FEF2F2 - 错误色背景
  errorBorder: globalColors.red100,     // #FEE2E2 - 错误色边框
  
  info: globalColors.blue500,           // #2563EB - 信息色 (使用主色)
  infoHover: globalColors.blue600,      // #1D4ED8 - 信息色悬停
  infoActive: globalColors.blue700,     // #1E40AF - 信息色激活
  infoBg: globalColors.blue50,          // #EFF6FF - 信息色背景
  infoBorder: globalColors.blue100,     // #DBEAFE - 信息色边框
  
  // 文本色别名 - 层次化文本颜色
  textBase: globalColors.gray900,       // #0F172A - 主要文本 (最高对比度)
  textSecondary: globalColors.gray700,  // #334155 - 次要文本 (标题)
  textTertiary: globalColors.gray600,   // #475569 - 辅助文本 (描述)
  textQuaternary: globalColors.gray500, // #64748B - 提示文本
  textDisabled: globalColors.gray400,   // #94A3B8 - 禁用文本
  textPlaceholder: globalColors.gray400, // #94A3B8 - 占位符文本
  textInverse: globalColors.white,      // #FFFFFF - 反色文本 (深色背景上)
  textLink: globalColors.blue600,       // #1D4ED8 - 链接文本
  textLinkHover: globalColors.blue700,  // #1E40AF - 链接悬停
  
  // 背景色别名 - 层次化背景系统
  bgBase: globalColors.white,           // #FFFFFF - 主背景 (页面基础)
  bgContainer: globalColors.white,      // #FFFFFF - 容器背景 (卡片、表单)
  bgElevated: globalColors.white,       // #FFFFFF - 悬浮背景 (模态框、下拉)
  bgLayout: globalColors.gray50,        // #F8FAFC - 布局背景 (页面区域)
  bgSection: globalColors.gray100,      // #F1F5F9 - 区块背景 (内容区域)
  bgSubtle: globalColors.gray50,        // #F8FAFC - 微妙背景 (表格行)
  bgMask: globalColors.blackAlpha[600], // rgba(0,0,0,0.48) - 遮罩背景
  bgOverlay: globalColors.blackAlpha[800], // rgba(0,0,0,0.80) - 覆盖背景
  
  // 边框色别名 - 层次化边框系统
  border: globalColors.gray200,         // #E2E8F0 - 默认边框
  borderSecondary: globalColors.gray100, // #F1F5F9 - 次要边框
  borderSubtle: globalColors.gray100,   // #F1F5F9 - 微妙边框
  borderStrong: globalColors.gray300,   // #CBD5E1 - 强调边框
  borderFocus: globalColors.blue500,    // #2563EB - 焦点边框
  borderDivider: globalColors.gray200,  // #E2E8F0 - 分割线
  
  // 交互状态色别名
  hover: globalColors.gray50,           // #F8FAFC - 通用悬停背景
  active: globalColors.gray100,         // #F1F5F9 - 通用激活背景
  selected: globalColors.blue50,        // #EFF6FF - 选中背景
  focus: globalColors.blue100,          // #DBEAFE - 焦点背景
  disabled: globalColors.gray100,       // #F1F5F9 - 禁用背景
} as const;

// 导出完整的颜色 Token
export const colorTokens: ColorTokens = {
  global: globalColors,
  alias: aliasColors,
};

// 暗色主题颜色 Token
export const darkColorTokens: ColorTokens = {
  global: globalColors, // 全局颜色保持不变
  alias: {
    // 品牌色保持不变，确保品牌一致性
    primary: globalColors.blue500,        // #2563EB - 主品牌色
    primaryHover: globalColors.blue400,   // #60A5FA - 悬停状态 (暗色主题下更亮)
    primaryActive: globalColors.blue600,  // #1D4ED8 - 激活状态
    primaryBg: 'rgba(37, 99, 235, 0.15)', // 主色背景 (暗色主题下更明显)
    primaryBorder: 'rgba(37, 99, 235, 0.3)', // 主色边框
    primaryText: globalColors.blue400,    // #60A5FA - 主色文本 (暗色主题下更亮)
    
    // 辅助色别名 - 深蓝系
    secondary: globalColors.darkBlue500,  // #1B4B8C - 辅助色
    secondaryHover: globalColors.darkBlue600, // #164A87 - 辅助色悬停
    secondaryActive: globalColors.darkBlue700, // #134082 - 辅助色激活
    secondaryBg: 'rgba(27, 75, 140, 0.15)', // 辅助色背景
    
    // 语义色别名 - 功能状态色
    success: globalColors.green500,       // #2E8B57 - 成功色
    successHover: globalColors.green600,  // #059669 - 成功色悬停
    successActive: globalColors.green700, // #047857 - 成功色激活
    successBg: 'rgba(46, 139, 87, 0.15)', // 成功色背景
    successBorder: 'rgba(46, 139, 87, 0.3)', // 成功色边框
    
    warning: globalColors.orange500,      // #FF6B35 - 警告色
    warningHover: globalColors.orange600, // #EA580C - 警告色悬停
    warningActive: globalColors.orange700, // #C2410C - 警告色激活
    warningBg: 'rgba(255, 107, 53, 0.15)', // 警告色背景
    warningBorder: 'rgba(255, 107, 53, 0.3)', // 警告色边框
    
    error: globalColors.red500,           // #EF4444 - 错误色
    errorHover: globalColors.red600,      // #DC2626 - 错误色悬停
    errorActive: globalColors.red700,     // #B91C1C - 错误色激活
    errorBg: 'rgba(239, 68, 68, 0.15)',  // 错误色背景
    errorBorder: 'rgba(239, 68, 68, 0.3)', // 错误色边框
    
    info: globalColors.blue500,           // #2563EB - 信息色
    infoHover: globalColors.blue400,      // #60A5FA - 信息色悬停
    infoActive: globalColors.blue600,     // #1D4ED8 - 信息色激活
    infoBg: 'rgba(37, 99, 235, 0.15)',   // 信息色背景
    infoBorder: 'rgba(37, 99, 235, 0.3)', // 信息色边框
    
    // 暗色主题文本色 - 层次化文本颜色
    textBase: globalColors.white,         // #FFFFFF - 主要文本
    textSecondary: globalColors.whiteAlpha[800], // rgba(255,255,255,0.80) - 次要文本
    textTertiary: globalColors.whiteAlpha[700],  // rgba(255,255,255,0.64) - 辅助文本
    textQuaternary: globalColors.whiteAlpha[600], // rgba(255,255,255,0.48) - 提示文本
    textDisabled: globalColors.whiteAlpha[400],   // rgba(255,255,255,0.24) - 禁用文本
    textPlaceholder: globalColors.whiteAlpha[500], // rgba(255,255,255,0.36) - 占位符文本
    textInverse: globalColors.gray900,    // #0F172A - 反色文本
    textLink: globalColors.blue400,       // #60A5FA - 链接文本
    textLinkHover: globalColors.blue300,  // #93C5FD - 链接悬停
    
    // 暗色主题背景色 - 层次化背景系统
    bgBase: '#0a0a0a',                    // 主背景 (更深的黑色)
    bgContainer: '#141414',               // 容器背景 (卡片、表单)
    bgElevated: '#1f1f1f',                // 悬浮背景 (模态框、下拉)
    bgLayout: '#000000',                  // 布局背景 (页面区域)
    bgSection: '#0f0f0f',                 // 区块背景 (内容区域)
    bgSubtle: '#0a0a0a',                  // 微妙背景 (表格行)
    bgMask: globalColors.blackAlpha[800], // rgba(0,0,0,0.80) - 遮罩背景
    bgOverlay: globalColors.blackAlpha[900], // rgba(0,0,0,0.92) - 覆盖背景
    
    // 暗色主题边框色 - 层次化边框系统
    border: '#262626',                    // 默认边框
    borderSecondary: '#1a1a1a',           // 次要边框
    borderSubtle: '#1a1a1a',              // 微妙边框
    borderStrong: '#404040',              // 强调边框
    borderFocus: globalColors.blue500,    // #2563EB - 焦点边框
    borderDivider: '#262626',             // 分割线
    
    // 交互状态色别名
    hover: '#1a1a1a',                     // 通用悬停背景
    active: '#262626',                    // 通用激活背景
    selected: 'rgba(37, 99, 235, 0.15)', // 选中背景
    focus: 'rgba(37, 99, 235, 0.25)',    // 焦点背景
    disabled: '#1a1a1a',                  // 禁用背景
  },
};

// 导出颜色常量，便于在其他地方使用
export const colorConstants = {
  // 品牌色常量 - 现代蓝系
  PRIMARY_BLUE: globalColors.blue500,        // #2563EB - 主品牌色
  PRIMARY_BLUE_LIGHT: globalColors.blue400,  // #60A5FA - 浅色变体
  PRIMARY_BLUE_DARK: globalColors.blue600,   // #1D4ED8 - 深色变体
  PRIMARY_BLUE_LIGHTER: globalColors.blue50, // #EFF6FF - 最浅背景
  
  // 辅助色常量 - 深蓝系
  SECONDARY_BLUE: globalColors.darkBlue500,  // #1B4B8C - 辅助色
  SECONDARY_BLUE_DARK: globalColors.darkBlue600, // #164A87 - 深色变体
  
  // 功能色常量 - 语义化颜色
  SUCCESS_COLOR: globalColors.green500,      // #2E8B57 - 成功色 (创新绿)
  WARNING_COLOR: globalColors.orange500,     // #FF6B35 - 警告色 (活力橙)
  ERROR_COLOR: globalColors.red500,          // #EF4444 - 错误色 (医用红)
  INFO_COLOR: globalColors.blue500,          // #2563EB - 信息色 (使用主色)
  
  // 文本色常量 - 层次化文本
  TEXT_PRIMARY: globalColors.gray900,        // #0F172A - 主要文本
  TEXT_SECONDARY: globalColors.gray700,      // #334155 - 次要文本
  TEXT_TERTIARY: globalColors.gray600,       // #475569 - 辅助文本
  TEXT_QUATERNARY: globalColors.gray500,     // #64748B - 提示文本
  TEXT_DISABLED: globalColors.gray400,       // #94A3B8 - 禁用文本
  TEXT_LINK: globalColors.blue600,           // #1D4ED8 - 链接文本
  
  // 背景色常量 - 层次化背景
  BG_PRIMARY: globalColors.white,            // #FFFFFF - 主背景
  BG_SECONDARY: globalColors.gray50,         // #F8FAFC - 次要背景
  BG_TERTIARY: globalColors.gray100,         // #F1F5F9 - 第三级背景
  BG_ELEVATED: globalColors.white,           // #FFFFFF - 悬浮背景
  
  // 边框色常量 - 层次化边框
  BORDER_PRIMARY: globalColors.gray200,      // #E2E8F0 - 主边框
  BORDER_SECONDARY: globalColors.gray100,    // #F1F5F9 - 次要边框
  BORDER_SUBTLE: globalColors.gray100,       // #F1F5F9 - 微妙边框
  BORDER_STRONG: globalColors.gray300,       // #CBD5E1 - 强调边框
  BORDER_FOCUS: globalColors.blue500,        // #2563EB - 焦点边框
} as const;